// src/pages/LoginPage.jsx
// Migrated from prototype/between-the-lines-v2.html lines 7002-7182.
// Modifications from verbatim per Sprint 2b-1 spec sections 3.4 + 1.5:
//   - Prop signature drops the page-setter prop in favor of state-only
//   - Adds react-router-dom useNavigate hook
//   - 1 navigation call-site converted to navigate('/dashboard') (prototype line 7015)
//   - Google sign-in toast text replaced with English (prototype line 7110) per Q-FS48
//
// Phase 2 Sprint 1 modifications:
//   - useAuth() hook replaces direct state.setUser call (auth state lives in AuthProvider)
//   - P10: signIn redirects to location.state.from (AuthGuard's intended destination) or /dashboard
//   - P8: Google Sign-In available only in emulator/firebase modes; disabled "setup pending" in mock
//   - P8: Google OAuth failure non-blocking — surfaces inline error; user can fall back to email/password
//   - MA-8 Option (a): sign-up `name` input kept visually; value not persisted in mock-mode
//     (deferred to Phase 2 Sprint 2 Firestore profile work)
//   - "Prototype mode: any email/password will work" footer line preserved (accurate for mock mode)

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconCheck } from '../components/icons.jsx';
import { useAuth } from '../auth/useAuth.js';
import { safeLoginReturnPath } from '../auth/loginReturnPath.js';
import LegalConsent from '../components/LegalConsent.jsx';
import { recordLegalConsent } from '../lib/callables.js';

export const LoginPage = ({ state }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { signIn, signUp, signInWithGoogle, sendPasswordReset, authMode } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    // 'signin' | 'signup'. `?mode=signup` (e.g. the home demo's "sign up free"
    // CTA) opens directly in signup mode; no param / any other value keeps the
    // signin default, so every existing link behaves exactly as before.
    const [mode, setMode] = useState(() => (
        new URLSearchParams(location.search).get('mode') === 'signup' ? 'signup' : 'signin'
    ));
    const [view, setView] = useState('auth');   // 'auth' | 'forgot' | 'reset-sent'
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [error, setError] = useState('');
    const [googleProviderAvailable, setGoogleProviderAvailable] = useState(false);
    // WP-3: age/parental + legal consent at signup (locked #15).
    const [consent, setConsent] = useState({ consented: false, versions: {} });

    // P8: Google Sign-In available only when authMode is emulator or firebase
    useEffect(() => {
        setGoogleProviderAvailable(authMode !== 'mock');
    }, [authMode]);

    const handleSubmit = async () => {
        if (!email || !password) return;
        setError('');
        try {
            if (mode === 'signup') {
                await signUp(email, password, name);
                // WP-3 clickwrap consent -- now FAIL-CLOSED (legal-consent-failclosed):
                // a signup MUST record consent. A failure keeps the user on this
                // screen (the outer catch sets `error` + does NOT navigate) instead
                // of letting them into the app with no recorded agreement. Skipped
                // only in mock (no backend). NOTE: the Auth account is created by
                // signUp ABOVE; on a consent failure the account exists but entry is
                // blocked here (front-door gate). A returning-session access-gate is
                // a SEPARATE follow-up (see the handback), not built here.
                if (authMode !== 'mock') {
                    // Each consent is { type, documentVersion } -- the field name the
                    // CF requires (recordLegalConsent.js) and the checkout path uses.
                    // BUG FIX: this was { type, version }, so the CF rejected EVERY
                    // signup consent as invalid-argument and the error was silently
                    // swallowed -> accounts created with NO consent recorded.
                    const consents = Object.entries(consent.versions).map(([type, documentVersion]) => ({ type, documentVersion }));
                    if (consents.length === 0) {
                        // Defensive fail-closed: the submit button is gated on consent,
                        // so this only fires on a state desync -- never let it through.
                        throw new Error('Please confirm you agree to the terms to create your account.');
                    }
                    try {
                        await recordLegalConsent({ consents, consentMethod: 'signup' });
                    } catch (e) {
                        console.error('recordLegalConsent (signup) failed:', e);
                        // Do NOT swallow -> fail closed with a friendly, code-free message.
                        throw new Error("We couldn't record your agreement to the terms. Please try again.");
                    }
                }
            } else {
                await signIn(email, password);
            }
            // P10: redirect to attempted destination (set by AuthGuard) or default /dashboard
            const destination = safeLoginReturnPath(location.state?.from);
            navigate(destination, { replace: true });
        } catch (err) {
            const fallbackMsg = mode === 'signup' ? 'Sign-up failed. Please try again.' : 'Sign-in failed. Please try again.';
            setError(err.message || fallbackMsg);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        try {
            await signInWithGoogle();
            // P10: same redirect logic
            const destination = safeLoginReturnPath(location.state?.from);
            navigate(destination, { replace: true });
        } catch (err) {
            // P8: Google OAuth failure is non-blocking — surface inline, user falls back to email/password
            setError(`Google Sign-In unavailable: ${err.message || 'unknown error'}. Please use email/password.`);
        }
    };

    const handleForgotPassword = () => {
        setError('');            // don't carry a sign-in error onto the reset view
        setResetEmail(email);
        setView('forgot');
    };

    // Wire the real Firebase reset email (was a fake setTimeout stub).
    const handleSendReset = async () => {
        if (!resetEmail || resetLoading) return;
        setError('');
        setResetLoading(true);
        try {
            await sendPasswordReset(resetEmail);
            setView('reset-sent');
        } catch (err) {
            const code = err && err.code;
            // PRIVACY: never reveal whether an email is registered. An unknown
            // account looks EXACTLY like success (also matches Firebase's own
            // email-enumeration-protection behavior, which resolves silently).
            if (code === 'auth/user-not-found' || code === 'auth/missing-email') {
                setView('reset-sent');
            } else if (code === 'auth/invalid-email') {
                setError("That doesn't look like a valid email address.");
            } else if (code === 'auth/too-many-requests') {
                setError('Too many attempts just now. Please wait a moment and try again.');
            } else {
                setError("We couldn't send the reset email. Please check your connection and try again.");
            }
        } finally {
            setResetLoading(false);
        }
    };

    const GoogleIcon = () => (
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    );

    /* ── RESET PASSWORD SCREEN ── */
    if (view === 'forgot') {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-10">
                        <div className="ornamental-divider mb-6 text-book-accent">
                            <span className="font-display text-xs font-semibold text-book-muted">Account</span>
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl text-book-text mb-3">Reset Password</h1>
                        <p className="font-serif italic text-book-muted">Enter your email address and we'll send you a link to reset your password.</p>
                    </div>
                    <div className="border border-book-accent/20 bg-book-page/30 p-8 space-y-5 decorative-corner text-book-accent">
                        <div>
                            <label className="block font-display text-book-muted text-xs mb-2 font-semibold">Email Address</label>
                            <input type="email" value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); setError(''); }}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendReset()}
                                className="w-full bg-book-pageDark border border-book-accent/30 px-4 py-3 text-book-text font-serif focus:outline-none focus:border-book-accent" placeholder="you@example.com" />
                        </div>
                        <button onClick={handleSendReset} disabled={resetLoading || !resetEmail}
                            className={`w-full py-3 font-display text-sm transition-all flex items-center justify-center gap-2 font-semibold ${resetLoading || !resetEmail ? 'bg-book-accent/50 text-black/60 cursor-not-allowed' : 'bg-book-accent text-black hover:bg-book-accentLight btn-sweep'}`}>
                            {resetLoading ? (
                                <><span className="inline-block w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></span> Sending…</>
                            ) : 'Send Reset Link'}
                        </button>
                        {/* Inline error (reuses the auth screen's red styling). Real
                            failures only -- an unknown email resolves as success. */}
                        {error && (
                            <div className="text-sm font-serif italic text-red-300 text-center">
                                {error}
                            </div>
                        )}
                        {/* Google users have no app password to reset. */}
                        <p className="font-serif italic text-book-muted text-xs text-center leading-relaxed">
                            Signed up with Google? Use "Continue with Google" instead — there's no separate password to reset.
                        </p>
                        <div className="text-center pt-1">
                            <button onClick={() => setView('auth')} className="font-serif italic text-book-muted text-sm hover:text-book-accent transition-colors">
                                ← Back to Sign In
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* ── RESET SENT SCREEN ── */
    if (view === 'reset-sent') {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md text-center">
                    <div className="border border-book-accent/20 bg-book-page/30 p-10 decorative-corner text-book-accent">
                        <div className="w-16 h-16 rounded-full border-2 border-book-accent flex items-center justify-center mx-auto mb-6">
                            <IconCheck className="w-8 h-8 text-book-accent" />
                        </div>
                        <div className="font-display text-book-accent text-xs mb-3 font-semibold">Check your email</div>
                        <h2 className="font-display text-2xl text-book-text mb-4">Reset link sent</h2>
                        <p className="font-serif italic text-book-muted leading-relaxed mb-2">
                            We've sent a password reset link to
                        </p>
                        <p className="font-display text-book-accent text-sm mb-8 break-all">{resetEmail}</p>
                        <button onClick={() => { setView('auth'); setPassword(''); }}
                            className="inline-flex items-center gap-2 px-5 py-3 border border-book-accent/40 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors font-semibold">
                            ← Back to Sign In
                        </button>
                    </div>
                    <p className="text-book-muted text-xs font-serif italic mt-6">Didn't receive it? Check your spam folder.</p>
                </div>
            </div>
        );
    }

    /* ── AUTH SCREEN (sign in / sign up) ── */
    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="ornamental-divider mb-6 text-book-accent">
                        <span className="font-display text-xs font-semibold text-book-muted">{mode === 'signin' ? 'Welcome' : 'Join'}</span>
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-book-text mb-3">
                        {mode === 'signin' ? 'Sign In' : 'Create Account'}
                    </h1>
                    <p className="font-serif italic text-book-muted">
                        {mode === 'signin' ? 'Welcome back to your library' : 'Begin your journey'}
                    </p>
                </div>
                <div className="border border-book-accent/20 bg-book-page/30 p-8 space-y-5 decorative-corner text-book-accent">
                    {mode === 'signup' && (
                        <div>
                            <label className="block font-display text-book-muted text-xs mb-2 font-semibold">Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                className="w-full bg-book-pageDark border border-book-accent/30 px-4 py-3 text-book-text font-serif focus:outline-none focus:border-book-accent" placeholder="Your name" />
                        </div>
                    )}
                    <div>
                        <label className="block font-display text-book-muted text-xs mb-2 font-semibold">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-book-pageDark border border-book-accent/30 px-4 py-3 text-book-text font-serif focus:outline-none focus:border-book-accent" placeholder="you@example.com" />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="font-display text-book-muted text-xs font-semibold">Password</label>
                            {mode === 'signin' && (
                                <button onClick={handleForgotPassword} className="font-serif italic text-book-muted text-xs hover:text-book-accent transition-colors">
                                    Forgot password?
                                </button>
                            )}
                        </div>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                            className="w-full bg-book-pageDark border border-book-accent/30 px-4 py-3 text-book-text font-serif focus:outline-none focus:border-book-accent" placeholder="••••••••" />
                    </div>
                    {mode === 'signup' && (
                        <LegalConsent theme="gold"
                            requireTypes={['terms_of_service', 'privacy_policy']}
                            onConsentChange={setConsent} />
                    )}

                    <button onClick={handleSubmit}
                        disabled={mode === 'signup' && !consent.consented}
                        className="w-full py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-book-muted">
                        {mode === 'signin' ? 'Sign In' : 'Create Account'}
                    </button>

                    {/* Phase 2 Sprint 1: inline error display (P8 non-blocking pattern) */}
                    {error && (
                        <div className="text-sm font-serif italic text-red-300 text-center">
                            {error}
                        </div>
                    )}

                    {/* OR divider */}
                    <div className="flex items-center gap-3">
                        <div className="flex-grow h-px bg-book-accent/20"></div>
                        <span className="font-display text-book-muted text-xs font-semibold">or</span>
                        <div className="flex-grow h-px bg-book-accent/20"></div>
                    </div>

                    {/* Google button — P8: real OAuth in emulator/firebase modes; disabled "setup pending" in mock */}
                    {googleProviderAvailable ? (
                        <button onClick={handleGoogleSignIn}
                            className="w-full py-2.5 bg-white text-[#3c4043] font-sans text-sm font-medium flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors border border-gray-300 shadow-sm rounded-sm">
                            <GoogleIcon />
                            Continue with Google
                        </button>
                    ) : (
                        <button disabled
                            className="w-full py-2.5 bg-white/60 text-[#3c4043]/50 font-sans text-sm font-medium flex items-center justify-center gap-3 border border-gray-300/50 rounded-sm cursor-not-allowed">
                            <GoogleIcon />
                            Continue with Google (setup pending)
                        </button>
                    )}

                    <div className="text-center pt-1">
                        <button onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} className="font-serif italic text-book-muted text-sm hover:text-book-accent transition-colors">
                            {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                        </button>
                    </div>
                </div>
                {authMode === 'mock' && (
                    <p className="text-center text-book-muted text-xs font-serif italic mt-6">
                        Prototype mode: any email/password will work.
                    </p>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
